import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  BlogPostFormData,
  BlogPostStatus,
  generateSlug,
  estimateReadTime,
  BlogPostImage
} from '../../types/blog-admin';
import ImageUpload from './ImageUpload';
import MarkdownEditor from './MarkdownEditor';
import {
  Save,
  Eye,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Tag,
  Search,
  Share2
} from 'lucide-react';

interface BlogPostFormProps {
  initialData?: Partial<BlogPostFormData>;
  onSubmit: (data: BlogPostFormData, status: BlogPostStatus) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export default function BlogPostForm({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false
}: BlogPostFormProps) {
  // Basic Content
  const [title, setTitle] = useState(initialData?.title || '');
  const [slug, setSlug] = useState(initialData?.slug || '');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [content, setContent] = useState(initialData?.content || '');
  const [excerpt, setExcerpt] = useState(initialData?.excerpt || '');
  const [author, setAuthor] = useState(initialData?.author || 'ProductWorkshop Team');

  // Organization
  const [tags, setTags] = useState<string[]>(initialData?.tags || []);
  const [tagInput, setTagInput] = useState('');
  const [category, setCategory] = useState(initialData?.category || '');
  const [readTime, setReadTime] = useState(initialData?.readTime || '');

  // Featured Image
  const [featuredImage, setFeaturedImage] = useState<BlogPostImage | undefined>(
    initialData?.featuredImage
  );
  const [imageAlt, setImageAlt] = useState(initialData?.featuredImage?.alt || '');

  // SEO - Essential
  const [metaDescription, setMetaDescription] = useState(initialData?.metaDescription || '');
  const [focusKeyword, setFocusKeyword] = useState(initialData?.focusKeyword || '');
  const [metaKeywords, setMetaKeywords] = useState<string[]>(initialData?.metaKeywords || []);
  const [keywordInput, setKeywordInput] = useState('');

  // SEO - Advanced
  const [showAdvancedSEO, setShowAdvancedSEO] = useState(false);
  const [metaTitle, setMetaTitle] = useState(initialData?.metaTitle || '');
  const [canonicalUrl, setCanonicalUrl] = useState(initialData?.canonicalUrl || '');
  const [ogTitle, setOgTitle] = useState(initialData?.ogTitle || '');
  const [ogDescription, setOgDescription] = useState(initialData?.ogDescription || '');
  const [ogImage, setOgImage] = useState(initialData?.ogImage || '');
  const [twitterTitle, setTwitterTitle] = useState(initialData?.twitterTitle || '');
  const [twitterDescription, setTwitterDescription] = useState(initialData?.twitterDescription || '');
  const [twitterImage, setTwitterImage] = useState(initialData?.twitterImage || '');

  // UI State
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Auto-generate slug from title
  useEffect(() => {
    if (title && !slugManuallyEdited) {
      setSlug(generateSlug(title));
    }
  }, [title, slugManuallyEdited]);

  // Auto-estimate read time
  useEffect(() => {
    if (content) {
      setReadTime(estimateReadTime(content));
    }
  }, [content]);

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) newErrors.title = 'Title is required';
    if (!slug.trim()) newErrors.slug = 'Slug is required';
    if (!content.trim()) newErrors.content = 'Content is required';
    if (!metaDescription.trim()) newErrors.metaDescription = 'Meta description is required for SEO';
    if (metaDescription.length < 50) newErrors.metaDescription = 'Meta description should be at least 50 characters';
    if (metaDescription.length > 160) newErrors.metaDescription = 'Meta description should be under 160 characters';
    if (!focusKeyword.trim()) newErrors.focusKeyword = 'Focus keyword is required for SEO';
    if (tags.length === 0) newErrors.tags = 'At least one tag is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (status: BlogPostStatus) => {
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const formData: BlogPostFormData = {
      slug,
      title,
      content,
      excerpt,
      author,
      tags,
      category,
      readTime,
      featuredImage: featuredImage ? { ...featuredImage, alt: imageAlt } : undefined,
      metaDescription,
      metaKeywords,
      focusKeyword,
      canonicalUrl,
      metaTitle,
      ogTitle,
      ogDescription,
      ogImage,
      twitterTitle,
      twitterDescription,
      twitterImage,
      status
    };

    await onSubmit(formData, status);
  };

  // Tag management
  const addTag = () => {
    const trimmedTag = tagInput.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      setTags([...tags, trimmedTag]);
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // Keyword management
  const addKeyword = () => {
    const trimmedKeyword = keywordInput.trim().toLowerCase();
    if (trimmedKeyword && !metaKeywords.includes(trimmedKeyword)) {
      setMetaKeywords([...metaKeywords, trimmedKeyword]);
      setKeywordInput('');
    }
  };

  const removeKeyword = (keywordToRemove: string) => {
    setMetaKeywords(metaKeywords.filter(keyword => keyword !== keywordToRemove));
  };

  return (
    <form className="space-y-8" onSubmit={(e) => e.preventDefault()}>
      {/* Basic Content Section */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-6">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          Basic Content
        </h2>

        {/* Title */}
        <div>
          <Label htmlFor="title" className="text-sm font-medium">
            Title *
          </Label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            placeholder="e.g., Product Feedback Loop: The Ultimate Guide"
          />
          {errors.title && <p className="text-sm text-destructive mt-1">{errors.title}</p>}
          <p className="text-xs text-muted-foreground mt-1">
            {title.length} characters • Optimal: 50-60 for SEO
          </p>
        </div>

        {/* Slug */}
        <div>
          <Label htmlFor="slug" className="text-sm font-medium">
            URL Slug *
          </Label>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm text-muted-foreground">/blog/</span>
            <input
              id="slug"
              type="text"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugManuallyEdited(true);
              }}
              className="flex-1 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              placeholder="product-feedback-loop-ultimate-guide"
            />
          </div>
          {errors.slug && <p className="text-sm text-destructive mt-1">{errors.slug}</p>}
          <p className="text-xs text-muted-foreground mt-1">
            Auto-generated from title. Use lowercase, hyphens, no special characters.
          </p>
        </div>

        {/* Excerpt */}
        <div>
          <Label htmlFor="excerpt" className="text-sm font-medium">
            Excerpt (Optional)
          </Label>
          <textarea
            id="excerpt"
            value={excerpt}
            onChange={(e) => setExcerpt(e.target.value)}
            rows={3}
            className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none"
            placeholder="Short summary of your post..."
          />
          <p className="text-xs text-muted-foreground mt-1">
            Optional. Used in preview cards if provided.
          </p>
        </div>

        {/* Author */}
        <div>
          <Label htmlFor="author" className="text-sm font-medium">
            Author
          </Label>
          <input
            id="author"
            type="text"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            placeholder="ProductWorkshop Team"
          />
        </div>

        {/* Content */}
        <div>
          <Label className="text-sm font-medium mb-2 block">
            Content *
          </Label>
          <MarkdownEditor value={content} onChange={setContent} />
          {errors.content && <p className="text-sm text-destructive mt-1">{errors.content}</p>}
        </div>
      </div>

      {/* Featured Image Section */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-6">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Share2 className="h-5 w-5 text-primary" />
          Featured Image
        </h2>

        <ImageUpload
          onImageUploaded={(url, storagePath) => {
            setFeaturedImage({ url, storagePath, alt: imageAlt });
          }}
          onImageRemoved={() => setFeaturedImage(undefined)}
          currentImageUrl={featuredImage?.url}
          currentStoragePath={featuredImage?.storagePath}
        />

        {featuredImage && (
          <div>
            <Label htmlFor="imageAlt" className="text-sm font-medium">
              Image Alt Text (SEO) *
            </Label>
            <input
              id="imageAlt"
              type="text"
              value={imageAlt}
              onChange={(e) => setImageAlt(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              placeholder="e.g., Diagram showing the 5 stages of a product feedback loop"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Describe the image for SEO and accessibility
            </p>
          </div>
        )}
      </div>

      {/* Organization Section */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-6">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Tag className="h-5 w-5 text-primary" />
          Organization
        </h2>

        {/* Tags */}
        <div>
          <Label htmlFor="tagInput" className="text-sm font-medium">
            Tags *
          </Label>
          <div className="flex gap-2 mt-1">
            <input
              id="tagInput"
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              className="flex-1 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              placeholder="Add a tag..."
            />
            <Button type="button" onClick={addTag} variant="outline">
              Add
            </Button>
          </div>
          {errors.tags && <p className="text-sm text-destructive mt-1">{errors.tags}</p>}
          <div className="flex flex-wrap gap-2 mt-2">
            {tags.map(tag => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
              >
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="hover:bg-primary/20 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Category */}
        <div>
          <Label htmlFor="category" className="text-sm font-medium">
            Category (Optional)
          </Label>
          <input
            id="category"
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            placeholder="e.g., Product Management, User Feedback"
          />
        </div>

        {/* Read Time */}
        <div>
          <Label htmlFor="readTime" className="text-sm font-medium">
            Read Time
          </Label>
          <input
            id="readTime"
            type="text"
            value={readTime}
            onChange={(e) => setReadTime(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            placeholder="Auto-calculated from content"
            disabled
          />
          <p className="text-xs text-muted-foreground mt-1">
            Automatically calculated based on word count (200 words/min)
          </p>
        </div>
      </div>

      {/* Essential SEO Section */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-6">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Search className="h-5 w-5 text-primary" />
          Essential SEO
        </h2>

        {/* Meta Description */}
        <div>
          <Label htmlFor="metaDescription" className="text-sm font-medium">
            Meta Description *
          </Label>
          <textarea
            id="metaDescription"
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)}
            rows={3}
            maxLength={160}
            className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none"
            placeholder="Write a compelling description that appears in search results..."
          />
          {errors.metaDescription && (
            <p className="text-sm text-destructive mt-1">{errors.metaDescription}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            {metaDescription.length}/160 characters • Shows in Google search results
          </p>
        </div>

        {/* Focus Keyword */}
        <div>
          <Label htmlFor="focusKeyword" className="text-sm font-medium">
            Focus Keyword *
          </Label>
          <input
            id="focusKeyword"
            type="text"
            value={focusKeyword}
            onChange={(e) => setFocusKeyword(e.target.value)}
            className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
            placeholder="e.g., product feedback loop"
          />
          {errors.focusKeyword && (
            <p className="text-sm text-destructive mt-1">{errors.focusKeyword}</p>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Primary keyword you want to rank for. Use naturally throughout the content.
          </p>
        </div>

        {/* Meta Keywords */}
        <div>
          <Label htmlFor="keywordInput" className="text-sm font-medium">
            Meta Keywords
          </Label>
          <div className="flex gap-2 mt-1">
            <input
              id="keywordInput"
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
              className="flex-1 px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
              placeholder="Add related keywords..."
            />
            <Button type="button" onClick={addKeyword} variant="outline">
              Add
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {metaKeywords.map(keyword => (
              <span
                key={keyword}
                className="inline-flex items-center gap-1 px-3 py-1 bg-muted text-foreground rounded-full text-sm"
              >
                {keyword}
                <button
                  type="button"
                  onClick={() => removeKeyword(keyword)}
                  className="hover:bg-muted-foreground/20 rounded-full p-0.5"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Related keywords and variations. Helps with semantic search.
          </p>
        </div>
      </div>

      {/* Advanced SEO Section (Collapsible) */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-6">
        <button
          type="button"
          onClick={() => setShowAdvancedSEO(!showAdvancedSEO)}
          className="w-full flex items-center justify-between text-xl font-bold"
        >
          <span className="flex items-center gap-2">
            <Search className="h-5 w-5 text-primary" />
            Advanced SEO (Optional)
          </span>
          {showAdvancedSEO ? (
            <ChevronUp className="h-5 w-5" />
          ) : (
            <ChevronDown className="h-5 w-5" />
          )}
        </button>

        {showAdvancedSEO && (
          <div className="space-y-6 pt-4 border-t border-border">
            {/* Meta Title Override */}
            <div>
              <Label htmlFor="metaTitle" className="text-sm font-medium">
                Meta Title Override
              </Label>
              <input
                id="metaTitle"
                type="text"
                value={metaTitle}
                onChange={(e) => setMetaTitle(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                placeholder="Leave blank to use post title"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Override the title tag. Leave blank to use post title.
              </p>
            </div>

            {/* Canonical URL */}
            <div>
              <Label htmlFor="canonicalUrl" className="text-sm font-medium">
                Canonical URL
              </Label>
              <input
                id="canonicalUrl"
                type="url"
                value={canonicalUrl}
                onChange={(e) => setCanonicalUrl(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                placeholder="https://getproductworkshop.com/blog/..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                For duplicate content protection. Usually auto-generated.
              </p>
            </div>

            {/* Open Graph Overrides */}
            <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
              <h3 className="font-medium text-sm">Open Graph (Social Sharing)</h3>

              <div>
                <Label htmlFor="ogTitle" className="text-sm">OG Title</Label>
                <input
                  id="ogTitle"
                  type="text"
                  value={ogTitle}
                  onChange={(e) => setOgTitle(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  placeholder="Leave blank to use post title"
                />
              </div>

              <div>
                <Label htmlFor="ogDescription" className="text-sm">OG Description</Label>
                <textarea
                  id="ogDescription"
                  value={ogDescription}
                  onChange={(e) => setOgDescription(e.target.value)}
                  rows={2}
                  className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none"
                  placeholder="Leave blank to use meta description"
                />
              </div>

              <div>
                <Label htmlFor="ogImage" className="text-sm">OG Image URL</Label>
                <input
                  id="ogImage"
                  type="url"
                  value={ogImage}
                  onChange={(e) => setOgImage(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  placeholder="Leave blank to use featured image"
                />
              </div>
            </div>

            {/* Twitter Card Overrides */}
            <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
              <h3 className="font-medium text-sm">Twitter Card</h3>

              <div>
                <Label htmlFor="twitterTitle" className="text-sm">Twitter Title</Label>
                <input
                  id="twitterTitle"
                  type="text"
                  value={twitterTitle}
                  onChange={(e) => setTwitterTitle(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  placeholder="Leave blank to use post title"
                />
              </div>

              <div>
                <Label htmlFor="twitterDescription" className="text-sm">Twitter Description</Label>
                <textarea
                  id="twitterDescription"
                  value={twitterDescription}
                  onChange={(e) => setTwitterDescription(e.target.value)}
                  rows={2}
                  className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background resize-none"
                  placeholder="Leave blank to use meta description"
                />
              </div>

              <div>
                <Label htmlFor="twitterImage" className="text-sm">Twitter Image URL</Label>
                <input
                  id="twitterImage"
                  type="url"
                  value={twitterImage}
                  onChange={(e) => setTwitterImage(e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background"
                  placeholder="Leave blank to use featured image"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-4 sticky bottom-0 bg-background border-t border-border p-4 -mx-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>

        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleSubmit('draft')}
            disabled={isLoading}
          >
            <Save className="h-4 w-4 mr-2" />
            Save as Draft
          </Button>
          <Button
            type="button"
            onClick={() => handleSubmit('published')}
            disabled={isLoading}
          >
            <Eye className="h-4 w-4 mr-2" />
            Publish
          </Button>
        </div>
      </div>
    </form>
  );
}
